import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowUpRight, Check, CircleDot } from 'lucide-react';
import { apiFetch } from '../../lib/api';
import { SITE_COLOR, SITE_ICON } from '../../lib/sites';
import { useTeamTasks, type TeamTaskItem } from '../../hooks/useTeamTasks';
import { useOAuthPopup } from '../../hooks/useOAuthPopup';
import { sourcesSurfaceQueryKey } from '../../hooks/useSourcesSurface';

/*
 * TasksPanel — the brief's bottom-left panel beside the Decision ledger (Teddy 2026-07-28:
 * renamed from the Linear placeholder to "Tasks" — the recipient's ACTIVE and ASSIGNED tracker
 * issues, each row carrying its tracker's REAL brand mark). Rows come from the live tasks rail
 * (GET /api/surfaces/team/tasks — Linear mirror today; a Jira/Asana row would arrive with its
 * own `tool` and pick up its glyph from lib/sites automatically).
 *
 * Write capability (Teddy 2026-07-28 — Terum's first outbound write): the check circle POSTs
 * /api/surfaces/team/tasks/complete, which marks the issue Done in Linear itself. A pre-write
 * (read-scope) connection gets a typed insufficient_scope back — the panel shows an honest
 * "reconnect Linear" line instead of a dead button. Optimistic strikethrough, rolled back on
 * error; the refetch drops the completed row (the rail selects open issues only).
 *
 * Static section sizing (Teddy 2026-07-27): the shell always renders — loading shows ghost
 * rows; an unconnected tracker or an empty list shows an honest line. Overdue/blocked accents
 * come from the rail's own mechanical facts (due_date + state), never a judgement made here.
 */

/** The tracker's brand chip — the REAL glyph from lib/sites (Linear's simple-icons mark on its
 * brand color). Unknown tools fall back to a neutral chip until their glyph lands in sites.ts. */
function ToolMark({ tool }: { tool: string }) {
  const icon = SITE_ICON[tool];
  const color = SITE_COLOR[tool];
  if (!icon || !color) {
    return (
      <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-muted text-muted-foreground">
        <CircleDot size={15} />
      </span>
    );
  }
  return (
    <span
      className="grid size-8 shrink-0 place-items-center rounded-lg text-white shadow-[0_4px_8px_rgba(0,0,0,.14)]"
      style={{ background: color }}
      data-testid={`tool-mark-${tool}`}
    >
      <svg viewBox={icon.viewBox} fill="currentColor" width={16} height={16}>
        {icon.paths.map((p, i) => (
          <path key={i} d={p.d} fillRule={p.fillRule} />
        ))}
      </svg>
    </span>
  );
}

function GhostTaskRow() {
  return (
    <div className="rounded-2xl border border-dashed border-[#eadfce] bg-[#fbf7ef]/60 p-4 dark:border-border dark:bg-muted/40">
      <div className="flex items-start gap-3">
        <span className="size-8 shrink-0 rounded-lg bg-muted/70 dark:bg-card/70" />
        <span className="min-w-0 flex-1 space-y-2 pt-1">
          <span className="block h-3 w-2/3 rounded bg-muted/70 dark:bg-card/70" />
          <span className="block h-3 w-1/3 rounded bg-muted/50 dark:bg-card/50" />
        </span>
      </div>
    </div>
  );
}

function TaskRow({ item, done, canWrite, onComplete }: { item: TeamTaskItem; done: boolean; canWrite: boolean; onComplete: () => void }) {
  const title = item.title || item.identifier || 'Untitled task';
  const flag = item.isOverdue ? 'overdue' : item.isBlocked ? 'blocked' : null;
  const meta = [item.state, item.dueDate ? `due ${item.dueDate}` : null].filter(Boolean).join(' · ');
  const content = (
    <>
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-2">
          {item.identifier && (
            <span className="shrink-0 font-['DM_Mono'] text-[10px] text-muted-foreground">{item.identifier}</span>
          )}
          <span className={`min-w-0 flex-1 truncate text-[15px] font-medium ${done ? 'line-through' : ''}`}>
            {title}
          </span>
        </span>
        {(meta || flag) && (
          <span className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
            {flag && (
              <span className={flag === 'overdue' ? 'font-medium text-[#bd5b4c]' : 'font-medium text-[#8b6546]'}>
                {flag}
              </span>
            )}
            {meta && <span className="truncate">{meta}</span>}
          </span>
        )}
      </span>
      {item.url && !done && (
        <ArrowUpRight size={14} className="mt-1 shrink-0 text-muted-foreground transition group-hover:text-foreground" />
      )}
    </>
  );
  return (
    <div
      className={`group flex w-full items-start gap-3 rounded-2xl border border-[#eadfce] bg-[#fbf7ef] p-3.5 text-left shadow-[0_5px_12px_rgba(88,69,42,.025)] transition dark:border-border dark:bg-muted ${done ? 'opacity-50' : ''}`}
      data-testid="task-row"
    >
      {/* The write verb: mark Done in the tracker itself — never a local-only hide. */}
      {done ? (
        <span className="mt-0.5 grid size-7 shrink-0 place-items-center rounded-full bg-muted text-muted-foreground">
          <Check size={13} strokeWidth={2.7} />
        </span>
      ) : (
        <button
          onClick={onComplete}
          disabled={!canWrite}
          aria-label={canWrite ? `Mark ${title} done` : `Reconnect Linear to mark ${title} done`}
          data-testid="task-check"
          className={`mt-0.5 grid size-7 shrink-0 place-items-center rounded-full border border-[#d7cab7] bg-[#fffaf2] text-transparent transition dark:border-border dark:bg-card ${canWrite ? 'hover:border-foreground hover:text-foreground' : 'cursor-not-allowed opacity-40'}`}
        >
          <Check size={13} strokeWidth={2.7} />
        </button>
      )}
      <ToolMark tool={item.tool} />
      {item.url && !done ? (
        <a
          href={item.url}
          target="_blank"
          rel="noreferrer"
          className="flex min-w-0 flex-1 items-start gap-2"
          data-testid="task-link"
        >
          {content}
        </a>
      ) : (
        <span className="flex min-w-0 flex-1 items-start gap-2">{content}</span>
      )}
    </div>
  );
}

/** The Tasks panel — active + assigned issues from the recipient's connected trackers. */
export function TasksPanel() {
  // isPending, not isLoading: a paused (offline-mount) query shows the skeleton, never a false
  // "nothing assigned" claim (same law as the stats board / ledger).
  const { data, isPending, error } = useTeamTasks();
  const queryClient = useQueryClient();
  // Optimistic check-off: strike the row immediately; the refetch drops it (open-only rail).
  const [doneIds, setDoneIds] = useState<Set<string>>(() => new Set());
  const [writeError, setWriteError] = useState<'scope' | 'failed' | null>(null);
  const complete = useMutation({
    mutationFn: (item: TeamTaskItem) =>
      apiFetch('/api/surfaces/team/tasks/complete', {
        method: 'POST',
        body: JSON.stringify({ tool: item.tool, issueId: item.issueId }),
      }),
    onMutate: (item: TeamTaskItem) => {
      setWriteError(null);
      setDoneIds(prev => new Set(prev).add(item.issueId));
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['surfaces', 'team-tasks'] }),
    onError: (err: unknown, item: TeamTaskItem) => {
      setDoneIds(prev => {
        const next = new Set(prev);
        next.delete(item.issueId);
        return next;
      });
      setWriteError(err instanceof Error && err.message.includes('insufficient_scope') ? 'scope' : 'failed');
    },
  });

  const { open: openOAuthPopup } = useOAuthPopup();
  const [reconnectError, setReconnectError] = useState<string | null>(null);
  // Reconnect Linear in place: the same popup contract Sources uses (opened synchronously on the
  // click, then navigated once the connect URL is minted — no JWT in the URL). The hook already
  // refetches Sources; the tasks rail needs its own invalidation so writeCapable re-derives.
  const reconnectLinear = () => {
    setReconnectError(null);
    openOAuthPopup(
      async () => {
        const { url } = await apiFetch<{ url: string }>('/api/surfaces/sources/linear-connect-url', { credentials: 'include' });
        return url;
      },
      (payload) => {
        if (!payload.ok) { setReconnectError(payload.error || 'Failed to reconnect Linear'); return; }
        setWriteError(null);
        queryClient.invalidateQueries({ queryKey: ['surfaces', 'team-tasks'] });
        queryClient.invalidateQueries({ queryKey: sourcesSurfaceQueryKey });
      }
    );
  };

  const tasks = data?.status === 'ready' ? (data.tasks ?? null) : null;
  const items = tasks?.items ?? [];
  const overflow = tasks ? Math.max(0, tasks.totalCount - items.length) : 0;
  // Only an explicit false pre-disables. undefined/null = unknown ⇒ enabled, and the rail's 403
  // insufficient_scope drives the same reconnect affordance reactively.
  const canWrite = tasks?.writeCapable !== false;

  let body;
  if (isPending) {
    body = (
      <>
        <GhostTaskRow />
        <GhostTaskRow />
      </>
    );
  } else if (error || !tasks) {
    body = (
      <>
        <p className="px-1 text-sm text-muted-foreground">The task rail is unavailable right now.</p>
        <GhostTaskRow />
        <GhostTaskRow />
      </>
    );
  } else if (!tasks.connected) {
    body = (
      <>
        <p className="px-1 text-sm text-muted-foreground">
          Nothing wired - connect Linear to see your active work here.
        </p>
        <GhostTaskRow />
        <GhostTaskRow />
      </>
    );
  } else if (items.length === 0) {
    body = (
      <>
        <p className="px-1 text-sm text-muted-foreground">Nothing active and assigned to you right now.</p>
        <GhostTaskRow />
        <GhostTaskRow />
      </>
    );
  } else {
    body = (
      <>
        {items.map(item => (
          <TaskRow
            key={`${item.tool}:${item.issueId}`}
            item={item}
            done={doneIds.has(item.issueId)}
            canWrite={canWrite}
            onComplete={() => complete.mutate(item)}
          />
        ))}
        {/* One shared affordance for BOTH read-only paths: the pre-emptive writeCapable:false and
            the reactive 403 insufficient_scope. A real button, never a dead sentence. */}
        {(!canWrite || writeError === 'scope') && (
          <div className="px-1 text-sm" data-testid="task-write-error">
            <p className="text-[#bd5b4c]">
              The Linear connection is read-only - reconnect to check tasks off.
            </p>
            <button
              onClick={reconnectLinear}
              data-testid="task-reconnect"
              className="mt-1 font-medium text-foreground underline underline-offset-2"
            >
              Reconnect Linear
            </button>
            {reconnectError && <p className="mt-1 text-muted-foreground">{reconnectError}</p>}
          </div>
        )}
        {writeError === 'failed' && (
          <p className="px-1 text-sm text-[#bd5b4c]" data-testid="task-write-error">
            Couldn&apos;t check that off - try again.
          </p>
        )}
        {overflow > 0 && (
          <p className="px-1 text-sm text-muted-foreground">+{overflow} more in your tracker</p>
        )}
      </>
    );
  }

  return (
    <section
      className="h-full rounded-[24px] border border-[#ded5c6] bg-[#fbf8f2] p-5 shadow-[0_14px_30px_rgba(88,69,42,.04)] sm:p-6 dark:border-border dark:bg-card"
      data-testid="sp-panel-tasks"
    >
      <div className="flex items-center gap-3">
        <span className="grid size-10 place-items-center rounded-xl bg-muted text-[#766b59] shadow-sm dark:text-muted-foreground">
          <CircleDot size={19} />
        </span>
        <div>
          <h2 className="font-semibold">Tasks</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">Active and assigned to you</p>
        </div>
      </div>
      <div className="mt-5 space-y-3">{body}</div>
    </section>
  );
}
